import { Button, Flex, Tag } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { DATE_RANGE_LABEL_KEYS } from "../../Files/helpers/date-range-filter.ts"
import {
  countActiveGlobalFilesFilters,
  EMPTY_GLOBAL_FILES_FILTER_STATE,
  type GlobalFilesFilters
} from "../types/global-files-filters.types.ts"

type GlobalFilesFilterChipKey =
  | "location"
  | "documentType"
  | "status"
  | "customers"
  | "date"
  | "attention"

interface GlobalFilesFilterChip {
  key: GlobalFilesFilterChipKey
  label: string
}

// {key, fallback} pairs, not a bare key — matches the drawer's own Location
// radio options, so a missing translation still reads as "Company" rather
// than the raw lowercase param value.
const LOCATION_LABELS: Record<string, { key: string; fallback: string }> = {
  company: { key: "globalFiles.filters.locationCompany", fallback: "Company" },
  project: { key: "globalFiles.filters.locationProject", fallback: "Project" },
  lead: { key: "globalFiles.filters.locationLead", fallback: "Lead" }
}

// Same {key, fallback} shape, matching the drawer's own Needs Attention
// options — and the `key`s `GlobalFilesOverviewView`'s health breakdown rows
// carry, for `needs_attention` itself.
const ATTENTION_LABELS: Record<string, { key: string; fallback: string }> = {
  needs_attention: { key: "globalFiles.filters.attentionAllIssues", fallback: "All issues" },
  awaiting_signature: { key: "globalFiles.health.awaitingSignature", fallback: "Awaiting signature" },
  review_overdue: { key: "globalFiles.health.reviewOverdue", fallback: "Review overdue" },
  missing_document_type: {
    key: "globalFiles.health.missingDocumentType",
    fallback: "Missing document type"
  },
  changes_requested: { key: "globalFiles.health.changesRequested", fallback: "Changes requested" }
}

/** Pure so the chip-removal/label logic can be unit tested without mounting
 * the drawer or the tab. */
const getGlobalFilesFilterChips = (
  filters: GlobalFilesFilters,
  t: (key: string, fallback?: string) => string
): GlobalFilesFilterChip[] => {
  const chips: GlobalFilesFilterChip[] = []

  if (filters.location) {
    const locationMeta = LOCATION_LABELS[filters.location]
    const locationLabel = t(locationMeta.key, locationMeta.fallback)
    const recordPart =
      filters.recordId && filters.recordLabel ? ` · ${filters.recordLabel}` : ""
    chips.push({
      key: "location",
      label: `${t("globalFiles.filters.location", "Location")}: ${locationLabel}${recordPart}`
    })
  }

  if (filters.documentType) {
    chips.push({
      key: "documentType",
      label: `${t("files.filters.documentType")}: ${filters.documentTypeLabel ?? filters.documentType}`
    })
  }

  if (filters.status) {
    chips.push({
      key: "status",
      label: `${t("files.filters.status")}: ${filters.statusLabel ?? filters.status}`
    })
  }

  if (filters.customerIds.length > 0) {
    const [first, ...rest] = filters.customerLabels.length
      ? filters.customerLabels
      : filters.customerIds
    const suffix = rest.length ? ` +${rest.length}` : ""
    chips.push({
      key: "customers",
      label: `${t("files.filters.customer")}: ${first}${suffix}`
    })
  }

  if (filters.dateRange) {
    const dateLabel =
      filters.dateRange === "custom" && filters.dateAfter && filters.dateBefore
        ? `${filters.dateAfter} – ${filters.dateBefore}`
        : t(`files.filters.dateRangePresets.${DATE_RANGE_LABEL_KEYS[filters.dateRange]}`)
    chips.push({
      key: "date",
      label: `${t("files.filters.date")}: ${dateLabel}`
    })
  }

  if (filters.attention) {
    const attentionMeta = ATTENTION_LABELS[filters.attention]
    chips.push({
      key: "attention",
      label: `${t("globalFiles.filters.attention", "Needs Attention")}: ${t(attentionMeta.key, attentionMeta.fallback)}`
    })
  }

  return chips
}

const removeGlobalFilesFilter = (
  filters: GlobalFilesFilters,
  key: GlobalFilesFilterChipKey
): GlobalFilesFilters => {
  switch (key) {
    case "location":
      // Removing Location drops its Project/Lead refinement too — there is
      // nothing for `recordId` to mean once `location` is unset.
      return { ...filters, location: null, recordId: null, recordLabel: null }
    case "documentType":
      return { ...filters, documentType: null, documentTypeLabel: null }
    case "status":
      return { ...filters, status: null, statusLabel: null }
    case "customers":
      return { ...filters, customerIds: [], customerLabels: [] }
    case "date":
      return { ...filters, dateRange: null, dateAfter: null, dateBefore: null }
    case "attention":
      return { ...filters, attention: null }
    default:
      return filters
  }
}

interface GlobalFilesActiveFilterChipsProps {
  filters: GlobalFilesFilters
  onChange: (filters: GlobalFilesFilters) => void
}

export function GlobalFilesActiveFilterChips({
  filters,
  onChange
}: GlobalFilesActiveFilterChipsProps) {
  const { t } = useTranslation()
  const chips = getGlobalFilesFilterChips(filters, t)

  if (!chips.length) return null

  return (
    <Flex gap={6} wrap="wrap" align="center">
      {chips.map((chip) => (
        <Tag
          key={chip.key}
          closable
          onClose={() => onChange(removeGlobalFilesFilter(filters, chip.key))}
        >
          {chip.label}
        </Tag>
      ))}
      {countActiveGlobalFilesFilters(filters) > 0 && (
        <Button type="link" size="small" onClick={() => onChange(EMPTY_GLOBAL_FILES_FILTER_STATE)}>
          {t("files.filters.clearAll")}
        </Button>
      )}
    </Flex>
  )
}

export { getGlobalFilesFilterChips, removeGlobalFilesFilter }
export type { GlobalFilesFilterChip, GlobalFilesFilterChipKey }
